import { z } from 'zod/v4';

/**
 * One `indexes` entry of a risk body, ported from `indexesProps` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk.js`. Every
 * field is optional; unknown keys are stripped, as AJV stripped them.
 */
export const RiskIndexInputSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  sourceType: z.enum(['main', 'additional', 'Main']).optional(),
  year: z.number().optional(),
  retracedScore: z.number().optional(),
  indexValue: z.number().optional(),
  link: z.string().optional(),
});

/**
 * An optional, nullable text field. Legacy AJV coerced an empty string to
 * `null` before its `minLength: 1` check, so a blank form field was stored as
 * NULL rather than rejected — both frontends send `''` for blanks.
 */
const optionalText = (max?: number) =>
  (max === undefined ? z.string() : z.string().max(max))
    .nullable()
    .optional()
    .transform(value => (value === '' ? null : value));

/**
 * The fields of a risk body, ported from `riskProperties` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk.js` — shared
 * by create and update. `impactAreaIds` stays legacy's comma-separated string.
 */
const riskBodyShape = {
  title: z.string().min(1).max(128),
  description: optionalText(),
  institute: optionalText(512),
  section: optionalText(128),
  impactAreaIds: z.string().min(1).optional(),
  indexes: z.array(RiskIndexInputSchema).optional(),
};

/**
 * Body of `POST /risk-analysis/risks`, ported from `create.body` in the same
 * legacy file. Unknown properties are stripped, not rejected, as AJV did.
 */
export const CreateRiskBodySchema = z.object(riskBodyShape);

export type CreateRiskBody = z.infer<typeof CreateRiskBodySchema>;

/**
 * Body of `PATCH /risk-analysis/risks/:id`, ported from `update.body` in the
 * same legacy file. Strict, unlike legacy AJV, which silently stripped unknown
 * properties.
 *
 * Every field but `title` is optional, and an omitted one keeps its stored
 * value. `impactAreaIds`, when given, is the complete set of links; omitted, the
 * links are left alone.
 */
export const UpdateRiskBodySchema = z.strictObject(riskBodyShape);

export type UpdateRiskBody = z.infer<typeof UpdateRiskBodySchema>;

export const UpdateRiskParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

/**
 * A risk as the legacy `risk` response definition sent it after a create —
 * `getRiskById` in `2025-06/risk-analysis-service/data/risks/get.js` through
 * `buildRisk`. The timestamps were selected but stripped by that definition,
 * and `riskGroup` was never selected; neither is sent. `impactAreaIds` is `[]`
 * without links; `indexes` is `null` when none were given.
 */
export const RiskResourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  institute: z.string().nullable(),
  section: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  isRemoved: z.number(),
  impactAreaIds: z.array(z.string()),
  indexes: z.array(z.record(z.string(), z.unknown())).nullable(),
});

export type RiskResource = z.infer<typeof RiskResourceSchema>;

export const RISKS_LIST_DEFAULT_PAGE_SIZE = 15;
export const RISKS_LIST_MAX_PAGE_SIZE = 500;
export const RISKS_LIST_FILTER_MAX_LENGTH = 512;

/**
 * `GET /risk-analysis/risks` querystring, ported from `list.querystring` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk.js`
 * (`...paginationParams, filter`).
 *
 * Strict, unlike legacy AJV, which silently stripped unknown params — so a
 * `sort` that could never take effect now fails loudly instead. There is no
 * `sort` param; the order is always `created_at`.
 */
export const RisksListQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(RISKS_LIST_MAX_PAGE_SIZE)
    .default(RISKS_LIST_DEFAULT_PAGE_SIZE),
  filter: z.string().max(RISKS_LIST_FILTER_MAX_LENGTH).optional(),
});

export type RisksListQuery = z.infer<typeof RisksListQuerySchema>;

/** The average likelihood score of one risk across the tenant's companies in one country. */
export const RiskNetRiskEntrySchema = z.object({
  countryCode: z.string(),
  netRisk: z.number().nullable(),
});

export type RiskNetRiskEntry = z.infer<typeof RiskNetRiskEntrySchema>;

/**
 * One row of `GET /risk-analysis/risks`, as the legacy `risk` response
 * definition sent it: `listQuery` in `data/risks/queries/get.js` through
 * `buildRisks`. The timestamps were selected but stripped by that definition,
 * so they are not sent.
 *
 * `riskGroup` is `''` when the column is NULL — legacy's serializer wrote a
 * NULL string field as `""`, and the old frontend groups its risk-landscape
 * columns by this key. `netRisk` is `[]` without a tenant, without a standard
 * blueprint, or without assessed companies.
 */
export const RiskListRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  institute: z.string().nullable(),
  section: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  impactAreaIds: z.array(z.string()),
  riskGroup: z.string(),
  netRisk: z.array(RiskNetRiskEntrySchema),
});

export type RiskListRow = z.infer<typeof RiskListRowSchema>;
